import { Fragment, useMemo, useContext } from 'react';
import { Tooltip } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { HISTORY, NOTE_ANALYTICS, NOTE_ANALYTICS_LOADER } from '../../../constants/patientChart.constant';
import { removeXSSAttacks } from '../../../common/helper';
import PatientChartContext from '../../../contexts/patientChart';

const backgroundClass = (index = 0) => {
  let className = '';
  if (index % 2 === 0) {
    className = 'bg-lightest-primary';
  }
  return className;
};

const COLUMNS = [
  {
    key: 'date',
    label: 'Date',
    className: 'min-width-80 max-width-80',
  },
  {
    key: 'oId',
    label: 'Order ID',
    className: 'min-width-80 max-width-80',
  },
  {
    key: 'products',
    label: 'Product(s)',
    className: 'flex flex-fill min-width-180',
  },
  {
    key: 'oType',
    label: 'Order Type',
    className: 'min-width-100 max-width-100',
  },
  {
    key: 'approval',
    label: 'Approval',
    className: 'min-width-120 max-width-120',
  },
  {
    key: 'status',
    label: 'Order Status',
    className: 'min-width-100 max-width-100',
  },
];

const HistoryHeading = () => (
  <div
    className={`
      flex align-items-center
      justify-content-flex-start
      height-38
      p-l-3
      p-r-3
      bg-lighter-grey
      sticky
    `}
  >
    <div className='flex flex-fill align-items-center'>
      {
        COLUMNS.map(({ key, label, className }) => (
          <div key={key} className={`font-roboto font-14 m-r-2 ${className}`}>{label}</div>
        ))
      }
    </div>
  </div>
);

const Loading = (
  <div className='flex flex-column flex-fill'>
    <HistoryHeading />
    <div className={`
      flex
      align-items-center
      justify-content-space-between
      height-38
      p-l-3
      p-r-3
      ${backgroundClass(0)}
    `}>
      <div className='flex flex-fill align-items-center'>
        <Skeleton variant='text' className='width-80 m-r-2' />
        <Skeleton variant='text' className='width-80 m-r-2' />
        <div className='flex flex-fill min-width-180 m-r-2'>
          <Skeleton variant='text' className='width-full' />
        </div>
        <Skeleton variant='text' className='width-100 m-r-2' />
        <Skeleton variant='text' className='width-120 m-r-2' />
        <Skeleton variant='text' className='width-100 m-r-2' />
      </div>
    </div>
  </div>
);

const NoHistory = (
  <div className='flex flex-column flex-fill'>
    <HistoryHeading />
    <div
      className={`
        flex
        flex-column
        align-items-flex-start
        justify-content-center
        in-progress
        p-l-3
        p-r-3
      `}
    >
      <div className='flex align-items-center height-40 font-roboto font-14'>No historical order for this patient.</div>
    </div>
  </div>
);

const HistoryUI = (item = { ...HISTORY(), patientId: 0, backgroundClass: '' }) => {
  const { noteAnalytics } = useContext(PatientChartContext);
  const { Products, backgroundClass, patientId, OrderId } = item;

  const analytics = useMemo(() => noteAnalytics.get(OrderId) || NOTE_ANALYTICS(), [noteAnalytics, OrderId]);

  const { products, orderTypes, approvals, tooltip } = useMemo(() => {
    const tooltip = {
      products: '',
      orderTypes: '',
      approvals: '',
    };

    const _products = [];
    let products = [<Fragment />];
    products = [];

    const _orderTypes = [];
    let orderTypes = [<Fragment />];
    orderTypes = [];

    const _approvals = [];
    let approvals = [<Fragment />];
    approvals = [];

    for (let i = 0; i < Products.length; i++) {
      const _p = Products[i];

      if (!_p?.ProductId) {
        continue;
      }

      _products.push(_p.displayTitle);

      products.push(
        <div key={_p.ProductId} className='height-30 line-30 min-width-180 overflow-ellipsis'>
          {_p.displayTitle}
        </div>
      );

      _orderTypes.push(_p.OrderType);

      orderTypes.push(
        <div key={_p.ProductId} className='height-30 line-30 min-width-100 max-width-100 overflow-ellipsis'>
          {_p.OrderType}
        </div>
      );

      let approval = _p.ApprovalStatus;

      // overrides when Disqualified
      const regex = /(no approval info)/gmi;
      if (analytics.approval.length && regex.test(approval)) {
        approval = analytics.approval;
      }

      _approvals.push(approval);

      approvals.push(
        <div
          key={_p.ProductId}
          className='height-30 line-30 min-width-120 max-width-120 overflow-ellipsis'
          dangerouslySetInnerHTML={{
            __html: removeXSSAttacks(approval),
          }}
        />
      );
    }

    tooltip.products = _products.join(', ');
    tooltip.orderTypes = _orderTypes.join(', ');
    tooltip.approvals = _approvals.join(', ');

    return {
      products,
      orderTypes,
      approvals,
      tooltip,
    };
  }, [Products, analytics]);

  const { last4DigitOrderId } = useMemo(() => {
    let last4DigitOrderId = 0;

    if (!OrderId) {
      return { last4DigitOrderId };
    }

    last4DigitOrderId = Number(OrderId) % 10000;

    return { last4DigitOrderId };
  }, [OrderId]);

  const handleLaunch = (item = HISTORY()) => {
    const URL = `/orderhistory/${item.OrderId}/${patientId}`;
    const dimension = {
      width: 1290,
      height: 780,
    };
    window.open(
      URL,
      '_blank',
      'toolbar=0,location=0,directories=0,status=1,menubar=0,titlebar=0,scrollbars=1,resizable=1,width=' + dimension.width + ',height=' + dimension.height
    );
  };

  return (
    <div className={`
      flex
      align-items-center
      justify-content-flex-start
      min-height-38
      p-l-3
      p-r-3
      m-t-2
      bg-lighter-grey
      ${backgroundClass}
    `}>
      <div className='flex flex-fill align-items-center'>
        <div className='min-width-80 max-width-80 font-roboto font-14 m-r-2 overflow-ellipsis'>{item.OrderDate}</div>

        <Tooltip title={OrderId} placement='top' arrow>
          <div
            className={`
              min-width-80
              max-width-80
              font-roboto
              font-14
              m-r-2
              overflow-ellipsis
              color-primary
              underline
              pointer
            `}
            onClick={handleLaunch.bind(this, { ...item })}
          >
            {last4DigitOrderId}
          </div>
        </Tooltip>

        <Tooltip title={tooltip.products} placement='top' arrow>
          <div className='flex flex-column flex-fill min-width-180 font-roboto font-14 m-r-2'>{products}</div>
        </Tooltip>

        <div className='flex flex-column min-width-100 max-width-100 font-roboto font-14 m-r-2'>{orderTypes}</div>

        <div className='flex flex-column min-width-120 max-width-120 font-roboto font-14 m-r-2'>{approvals}</div>

        <div className='min-width-100 max-width-100 font-roboto font-14 m-r-2 overflow-ellipsis'>{item.OrderStatus}</div>
      </div>
    </div>
  );
};

export const History = ({
  tabKey = '',
  selectedKey = '',
  data: {
    patientId = 0,
    history = {
      data: [HISTORY()],
      called: false,
      loading: false,
    },
  },
}) => {
  const { data: items, called, loading } = history;
  const { loading: globalLoading } = useContext(PatientChartContext);

  const displayClass = useMemo(() => tabKey !== selectedKey ? 'd-none' : '', [tabKey, selectedKey]);

  const loadingAnalytics = useMemo(() => globalLoading.has(NOTE_ANALYTICS_LOADER), [globalLoading]);

  const status = useMemo(() => `${called}-${loading || loadingAnalytics}-${!!items.length}`, [called, loading, loadingAnalytics, items]);

  const History = (
    <div className='flex flex-column flex-fill'>
      <HistoryHeading />

      {
        items.map((item, index) => (
          <HistoryUI
            key={item.OrderId}
            backgroundClass={backgroundClass(index)}
            patientId={patientId}
            {...item}
          />
        ))
      }
    </div>
  );

  const Component = new Map([
    ['true-false-true', History],
    ['true-false-false', NoHistory],
  ]);

  const ComponentWithLoading = new Map([
    [true, Component.get(status)],
    [false, Loading],
  ]);

  return (
    <div className={`flex flex-column flex-fill column-content scroll-y-auto ${displayClass}`}>
      {
        ComponentWithLoading.get(
          Component.has(status)
        )
      }
    </div>
  );
};

export default History;
